import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Clock, Users, Gauge, Check, ArrowLeft, Minus, Plus, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Book() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    date: "", time_slot: "", num_people: 1,
    customer_name: "", customer_email: "", customer_phone: "", notes: "",
  });

  useEffect(() => {
    api.get(`/tours/${slug}`)
      .then((res) => setTour(res.data))
      .catch(() => setTour(false))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = tour ? tour.price * form.num_people : 0;
  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_slot || !form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/checkout", {
        tour_slug: slug,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="App"><Navbar />
        <div className="min-h-screen flex items-center justify-center font-mono text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!tour) {
    return (
      <div className="App"><Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
          <h1 className="font-display font-bold text-3xl">Tour not found</h1>
          <Link to="/" className="text-signal font-mono text-sm uppercase tracking-widest">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="App bg-white">
      <Navbar />
      <div className="pt-16 md:pt-20">
        {/* Hero band */}
        <div className="relative h-[40vh] md:h-[55vh] overflow-hidden">
          <img src={tour.image} alt={tour.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#12414d]/55" />
          <div className="absolute inset-0 max-w-[1400px] mx-auto w-full px-6 md:px-12 flex flex-col justify-end pb-10">
            <Link to="/#tours" data-testid="back-to-tours" className="inline-flex items-center gap-2 text-white/80 font-mono text-xs uppercase tracking-widest mb-6 hover:text-signal transition-colors w-fit">
              <ArrowLeft size={14} /> All tours
            </Link>
            <h1 className="text-white font-display font-black tracking-tighter leading-none text-4xl md:text-7xl" data-testid="tour-title">
              {tour.name}
            </h1>
            <p className="text-white/80 text-lg mt-3">{tour.tagline}</p>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid lg:grid-cols-12 gap-12">
          {/* Details */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-6 font-mono text-xs uppercase tracking-widest text-muted-foreground border-b border-border pb-8">
              <span className="flex items-center gap-2"><Clock size={15} /> {tour.duration}</span>
              <span className="flex items-center gap-2"><Gauge size={15} /> {tour.difficulty}</span>
              <span className="flex items-center gap-2"><Users size={15} /> Max {tour.max_group} riders</span>
            </div>
            <p className="text-lg leading-relaxed mt-8 text-void/80 whitespace-pre-line">{tour.description}</p>

            <h3 className="font-display font-semibold text-xl mt-10 mb-5">Highlights</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {tour.highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 text-void/80">
                  <span className="text-signal"><Check size={18} /></span> {h}
                </li>
              ))}
            </ul>
          </div>

          {/* Booking form */}
          <div className="lg:col-span-5">
            <form
              onSubmit={handleSubmit}
              data-testid="booking-form"
              className="border border-border p-6 md:p-8 lg:sticky lg:top-28"
            >
              <div className="flex items-baseline justify-between border-b border-border pb-5 mb-6">
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">From</span>
                <span><span className="font-display font-black text-3xl tracking-tighter">${tour.price.toFixed(0)}</span> <span className="text-muted-foreground text-sm">/ person</span></span>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Date *</Label>
                  <Input
                    type="date" min={today} value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                    data-testid="booking-date" required
                    className="rounded-none mt-2 focus-visible:ring-signal"
                  />
                </div>

                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Time *</Label>
                  <Select value={form.time_slot} onValueChange={(v) => set("time_slot", v)}>
                    <SelectTrigger data-testid="booking-time" className="rounded-none mt-2 focus:ring-signal">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {(tour.time_slots || []).map((t) => (
                        <SelectItem key={t} value={t} data-testid={`time-${t}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Riders *</Label>
                  <div className="flex items-center border border-input mt-2">
                    <button type="button" data-testid="people-minus"
                      onClick={() => set("num_people", Math.max(1, form.num_people - 1))}
                      className="px-4 py-3 hover:bg-secondary transition-colors"><Minus size={16} /></button>
                    <span className="flex-1 text-center font-display font-bold text-lg" data-testid="people-count">{form.num_people}</span>
                    <button type="button" data-testid="people-plus"
                      onClick={() => set("num_people", Math.min(tour.max_group, form.num_people + 1))}
                      className="px-4 py-3 hover:bg-secondary transition-colors"><Plus size={16} /></button>
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Full name *</Label>
                  <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)}
                    data-testid="booking-name" required placeholder="Jane Rider"
                    className="rounded-none mt-2 focus-visible:ring-signal" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Email *</Label>
                  <Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)}
                    data-testid="booking-email" required placeholder="jane@email.com"
                    className="rounded-none mt-2 focus-visible:ring-signal" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Phone *</Label>
                  <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)}
                    data-testid="booking-phone" required placeholder="+1 (780) 555-0123"
                    className="rounded-none mt-2 focus-visible:ring-signal" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    data-testid="booking-notes" placeholder="Anything we should know?"
                    className="rounded-none mt-2 focus-visible:ring-signal" />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-border mt-6 pt-5">
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Total</span>
                <span className="font-display font-black text-3xl tracking-tighter" data-testid="booking-total">${total.toFixed(2)}</span>
              </div>

              <button
                type="submit" disabled={submitting}
                data-testid="booking-submit-button"
                className="w-full bg-signal text-white mt-5 py-5 text-sm font-bold uppercase tracking-widest hover:bg-void transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : "Pay & Book"}
              </button>
              <p className="text-center text-xs text-muted-foreground mt-3 font-mono">Secure checkout via Stripe</p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
